import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Tooltip,
  useTheme
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  Schedule,
  CheckCircle,
  Warning,
  Error,
  Assignment,
  Group,
  OpenInNew,
  TrendingUp,
  PlayArrow,
  Pause,
  Done
} from '@mui/icons-material';
import { getStatusHexColor, getIndicatorColor } from '../../utils/statusColors';

interface TimelineItem {
  key: string;
  summary: string;
  startDate?: string;
  endDate?: string;
  status: string;
  progress: number;
  riskLevel: 'low' | 'medium' | 'high';
  storyPoints?: {
    total: number;
    completed: number;
  };
  team?: string;
  url?: string;
}

interface TimelineViewProps {
  items: TimelineItem[];
  title?: string;
}

// Define pulse animation
const pulse = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const TimelineView: React.FC<TimelineViewProps> = ({
  items,
  title = "Epic Timeline"
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const getStatusIcon = (status: string, progress: number) => {
    // Check progress first
    if (progress === 100) return <Done color="success" fontSize="small" />;

    // Check status categories
    const statusLower = status.toLowerCase();
    if (statusLower.includes('done') || statusLower.includes('complete')) {
      return <Done color="success" fontSize="small" />;
    }
    if (statusLower.includes('progress') || statusLower.includes('active')) {
      return <PlayArrow color="primary" fontSize="small" />;
    }
    if (statusLower.includes('paused') || statusLower.includes('hold') || statusLower.includes('blocked')) {
      return <Pause color="warning" fontSize="small" />;
    }
    if (statusLower.includes('overdue') || statusLower.includes('late')) {
      return <Error color="error" fontSize="small" />;
    }
    if (statusLower.includes('risk') || statusLower.includes('warning')) {
      return <Warning color="warning" fontSize="small" />;
    }

    // Default for new/to-do items
    return <Schedule color="action" fontSize="small" />;
  };

  // Get color for status badge - always reflects actual status, not progress
  const getStatusBadgeColor = (status: string) => {
    return getStatusHexColor(status, isDarkMode);
  };

  // Get color for progress indicators (bar and percentage) - reflects completion
  const getProgressColor = (progress: number) => {
    if (progress === 100) return getStatusHexColor('done', isDarkMode);
    if (progress >= 75) return getStatusHexColor('in progress', isDarkMode);
    if (progress >= 50) return getStatusHexColor('in progress', isDarkMode);
    return getStatusHexColor('in progress', isDarkMode);
  };

  // Get color for timeline dot - reflects actual status
  const getTimelineDotColor = (status: string) => {
    return getStatusHexColor(status, isDarkMode);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateDuration = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isItemActiveToday = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return false;
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Set time to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    return today >= start && today <= end;
  };

  const getItemTimeStatus = (startDate?: string, endDate?: string) => {
    if (!startDate) return 'unknown';

    const today = new Date();
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;

    // Set time to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    if (end) end.setHours(0, 0, 0, 0);

    if (end && today >= start && today <= end) return 'active';
    if (today < start) return 'future';
    if (end && today > end) return 'past';
    if (!end && today >= start) return 'ongoing';

    return 'unknown';
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
        </Box>

        <Box sx={{ position: 'relative' }}>
          {/* Timeline line */}
          <Box
            sx={{
              position: 'absolute',
              left: 20,
              top: 0,
              bottom: 0,
              width: 2,
              bgcolor: 'divider',
              zIndex: 0
            }}
          />
          
          {items.map((item, index) => {
            const duration = calculateDuration(item.startDate, item.endDate);
            const timeStatus = getItemTimeStatus(item.startDate, item.endDate);
            const isActiveToday = timeStatus === 'active';

            return (
              <Box
                key={item.key}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  mb: 3,
                  pl: 5,
                  // Highlight items that are active today
                  ...(isActiveToday && {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: -8,
                      bottom: -8,
                      bgcolor: 'error.50',
                      borderRadius: 1,
                      zIndex: -1,
                      border: '1px solid',
                      borderColor: 'error.200'
                    }
                  })
                }}
              >
                {/* Today's Active Indicator */}
                {isActiveToday && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: -8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      zIndex: 2
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'error.main',
                        animation: `${pulse} 1.5s infinite`
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'error.main',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      }}
                    >
                      Active
                    </Typography>
                  </Box>
                )}

                {/* Timeline dot */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 12,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    border: 2,
                    borderColor: isActiveToday
                      ? 'error.main'
                      : getTimelineDotColor(item.status),
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // Add glow effect for active items
                    ...(isActiveToday && {
                      boxShadow: '0 0 8px rgba(244, 67, 54, 0.4)'
                    })
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: isActiveToday
                        ? 'error.main'
                        : getTimelineDotColor(item.status)
                    }}
                  />
                </Box>
                
                {/* Content */}
                <Card 
                  variant="outlined" 
                  sx={{ 
                    flex: 1,
                    ml: 2,
                    '&:hover': {
                      boxShadow: 1
                    }
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {getStatusIcon(item.status, item.progress)}
                      <Typography
                        variant="subtitle2"
                        sx={{ ml: 1, fontWeight: 'bold', color: 'primary.main' }}
                      >
                        {item.url ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                              {item.key}
                            </a>
                            <OpenInNew sx={{ ml: 0.5, fontSize: 14 }} />
                          </Box>
                        ) : (
                          item.key
                        )}
                      </Typography>
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {/* Epic Status Chip */}
                        <Chip
                          label={item.status}
                          size="small"
                          sx={{
                            backgroundColor: getStatusBadgeColor(item.status),
                            color: 'white',
                            fontWeight: 500,
                            '&:hover': {
                              backgroundColor: getStatusBadgeColor(item.status)
                            }
                          }}
                        />

                        {/* Time Status Chip */}
                        {timeStatus !== 'unknown' && (
                          <Chip
                            label={
                              timeStatus === 'active' ? 'Active Today' :
                              timeStatus === 'future' ? 'Upcoming' :
                              timeStatus === 'past' ? 'Completed' :
                              timeStatus === 'ongoing' ? 'In Progress' : ''
                            }
                            size="small"
                            color={
                              timeStatus === 'active' ? 'error' :
                              timeStatus === 'future' ? 'info' :
                              timeStatus === 'past' ? 'success' : 'warning'
                            }
                            variant={timeStatus === 'active' ? 'filled' : 'outlined'}
                            sx={{
                              ...(timeStatus === 'active' && {
                                animation: `${pulse} 2s infinite`,
                                fontWeight: 600
                              })
                            }}
                          />
                        )}

                        {item.storyPoints && (
                          <Chip
                            icon={<Assignment />}
                            label={`${item.storyPoints.completed}/${item.storyPoints.total} SP`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        <Chip
                          icon={<TrendingUp />}
                          label={`${item.progress}%`}
                          size="small"
                          sx={{
                            backgroundColor: getProgressColor(item.progress),
                            color: 'white',
                            fontWeight: 500,
                            border: 'none',
                            '&:hover': {
                              backgroundColor: getProgressColor(item.progress)
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {item.summary}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Tooltip title="Start Date">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Schedule sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(item.startDate)}
                          </Typography>
                        </Box>
                      </Tooltip>

                      <Typography variant="caption" color="text.secondary">
                        →
                      </Typography>

                      <Tooltip title="End Date">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircle sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(item.endDate)}
                          </Typography>
                        </Box>
                      </Tooltip>

                      {duration && (
                        <Tooltip title="Duration">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Schedule sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {duration} days
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}

                      {item.team && (
                        <Tooltip title="Assigned Team">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Group sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {item.team}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                    
                    {/* Progress bar */}
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 4,
                          bgcolor: 'grey.200',
                          borderRadius: 2,
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            width: `${item.progress}%`,
                            height: '100%',
                            bgcolor: getProgressColor(item.progress),
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
        
        {items.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No timeline data available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TimelineView;
